import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { InstallPromptService } from '../../core/pwa/install-prompt.service';

/**
 * Modal customizado pré-prompt nativo. Apresenta valor da instalação
 * (notificações, abre rápido) antes de invocar o prompt do browser.
 *
 * Aparece SÓ quando `service.shouldOffer()` for true:
 *   - browser disparou `beforeinstallprompt`
 *   - usuário concluiu onboarding + 30s
 *   - não dismissado nas últimas 7d
 *   - app não está rodando como PWA instalado
 *
 * Mount: `<app-install-prompt />` no `App` root component, sobreposto
 * a qualquer rota.
 */
@Component({
  selector: 'app-install-prompt',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (service.shouldOffer()) {
      <div
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-title"
        aria-describedby="install-desc"
      >
        <div class="w-full max-w-sm bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl p-6">
          <div class="text-5xl text-center mb-4" aria-hidden="true">📲</div>
          <h2
            id="install-title"
            class="text-xl font-bold text-center text-neutral-900 dark:text-neutral-100 mb-2"
          >
            Adicione à tela inicial
          </h2>
          <p
            id="install-desc"
            class="text-sm text-center text-neutral-600 dark:text-neutral-400 mb-6"
          >
            Abre rápido, recebe notificações de palpites e funciona offline.
          </p>

          <div class="space-y-2">
            <button
              type="button"
              (click)="install()"
              class="w-full px-4 py-3 rounded-lg bg-brand-500 hover:bg-brand-600 text-white font-semibold min-h-[44px]"
            >
              Instalar
            </button>
            <button
              type="button"
              (click)="dismiss()"
              class="w-full px-4 py-3 rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 min-h-[44px]"
            >
              Agora não
            </button>
          </div>
        </div>
      </div>
    }
  `,
})
export class InstallPromptComponent {
  protected readonly service = inject(InstallPromptService);

  async install(): Promise<void> {
    await this.service.presentNativePrompt();
  }

  dismiss(): void {
    this.service.dismiss();
  }
}
